import { useEffect, useMemo, useState } from 'react';
import { cellsToGrid } from '../lib/marching-cubes';
import { gridToScene, meshToScene } from '../lib/iso-scene';
import { run, isAbortError } from '../lib/worker-pool';

/**
 * Iso-surface of a 3D inversion model (docs/TODO.md 3.8). The cell grid is built here, the
 * marching-cubes pass runs in the worker pool (task `isosurface`, input buffer transferred,
 * result buffers transferred back) and the mesh is mapped into the scene frame.
 *
 * `levelT` (0..1) places the iso level within the model's value range (log10 for resistivity).
 * `enabled` builds the grid (needed for the plane slice); `extract` runs the surface pass.
 * Returns { grid, aff, bounds, level, mesh, busy, error }.
 */
export default function useIsoSurface({ entry, origin, enabled, extract = enabled, levelT }) {
  const [state, setState] = useState({ mesh: null, busy: false, error: null });

  const built = useMemo(() => {
    if (!enabled || !entry?.cells?.length || !origin) return { grid: null, aff: null, error: null };
    try {
      const grid = cellsToGrid(entry.cells, 'resistivity');
      if (!grid) return { grid: null, aff: null, error: 'Not a 3D model (no y coordinate)' };
      const aff = gridToScene(entry.cells, grid, origin);
      return { grid, aff, error: aff ? null : 'Model has no georeference (lon/lat)' };
    } catch (e) {
      return { grid: null, aff: null, error: e.message };
    }
  }, [enabled, entry, origin]);
  const { grid, aff } = built;

  const level = grid ? grid.range[0] + levelT * (grid.range[1] - grid.range[0]) : null;

  const bounds = useMemo(() => {
    if (!grid || !aff) return null;
    const { x, y, z } = grid.coords;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const mx of [x[0], x[x.length - 1]]) for (const my of [y[0], y[y.length - 1]]) for (const md of [z[0], z[z.length - 1]]) {
      const p = [aff.a * mx + aff.b * my + aff.tx, -md, aff.c * mx + aff.d * my + aff.tz];
      for (let k = 0; k < 3; k++) { min[k] = Math.min(min[k], p[k]); max[k] = Math.max(max[k], p[k]); }
    }
    return { min, max };
  }, [grid, aff]);

  useEffect(() => {
    if (!extract || !grid || !aff || level === null) { setState((s) => (s.mesh || s.busy ? { mesh: null, busy: false, error: null } : s)); return undefined; }
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const values = grid.values.slice(); // the worker gets (and owns) this copy
        const res = await run('isosurface',
          { values, dims: grid.dims, coords: grid.coords, iso: level },
          { label: 'Iso-surface', signal: ctrl.signal, transfer: [values.buffer] });
        if (!cancelled) setState({ mesh: res.triangleCount ? meshToScene(res, aff) : null, busy: false, error: res.triangleCount ? null : 'No surface at this level' });
      } catch (e) {
        if (!cancelled && !isAbortError(e)) setState({ mesh: null, busy: false, error: e.message });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); ctrl.abort(); };
  }, [extract, grid, aff, level]);

  return { grid, aff, bounds, level, mesh: state.mesh, busy: state.busy, error: built.error || state.error };
}
